import type { ISODateTimeString } from '@gat-ship/types'
import type { EstimatedTimeChoice } from '@gat-ship/ref-data'
import {
  combineLocalDateTimeToInstant,
  normalizeTimeString
} from '@gat-ship/utils'

/**
 * Precision of an ETA value, mirroring the API's
 * `PortCalls.Common.EstimatePrecision` enum. The picker/field works in
 * these terms directly:
 * - `date-only` — a calendar day with no time-of-day.
 * - `exact-time` — a calendar day + an exact wall-clock time.
 * - `named-time` — a calendar day + a named estimated time (e.g. "Noon").
 */
export type EtaPrecision = 'date-only' | 'named-time' | 'exact-time'

/**
 * The editable state of one ETA field. Carries the raw user choices; the
 * bridge turns it into the `{ instant, precision }` pair the payload
 * needs. The `name` is the estimated-time-name picker value
 * ({@link EstimatedTimeNameRef.name}), resolved to a reference time by
 * the consumer at submit.
 */
export interface EtaFieldValue {
  precision: EtaPrecision
  /** UTC instant carrying the chosen calendar day (noon-anchored). */
  date?: ISODateTimeString
  /** `HH:mm` wall-clock, for `exact-time`. */
  time?: string
  /** Selected estimated-time name, for `named-time`. */
  name?: string
}

/** A fresh, empty ETA field defaulting to the date-only mode. */
export function emptyEtaField(): EtaFieldValue {
  return { precision: 'date-only' }
}

/** True when the user has entered nothing in the field. */
export function isEtaFieldEmpty(field: EtaFieldValue | undefined): boolean {
  if (!field) return true
  return !field.date && !field.time && !field.name
}

/**
 * True when the field holds everything its precision needs to resolve to
 * an instant. `named-time` only needs a `name` here — the reference time
 * is looked up from the registry at resolve time.
 */
export function isEtaFieldComplete(field: EtaFieldValue | undefined): boolean {
  if (!field?.date) return false
  switch (field.precision) {
    case 'date-only':
      return true
    case 'exact-time':
      return Boolean(normalizeTimeString(field.time ?? ''))
    case 'named-time':
      return Boolean(field.name)
  }
}

/**
 * The time portion of an {@link EtaFieldValue} — `precision`, `time`, and
 * `name` — without touching `date`. Returned by {@link etaPatchFromTimeChoice}
 * so the consumer can spread it over the current field.
 */
export type EtaTimePatch = Pick<EtaFieldValue, 'precision' | 'time' | 'name'>

/**
 * Maps the combined estimated-time picker's {@link EstimatedTimeChoice} onto
 * the time portion of an ETA field. An `invalid` (unresolved) draft is stored
 * as an `exact-time` with its raw text — non-empty and non-normalizable, so
 * {@link isEtaFieldComplete} reads it as incomplete and `validateEtaSlot`
 * flags it, keeping the user's text on screen.
 */
export function etaPatchFromTimeChoice(
  choice: EstimatedTimeChoice
): EtaTimePatch {
  switch (choice?.kind) {
    case 'exact':
      return { precision: 'exact-time', time: choice.time, name: undefined }
    case 'named':
      return { precision: 'named-time', name: choice.name, time: undefined }
    case 'invalid':
      return { precision: 'exact-time', time: choice.text, name: undefined }
    default:
      return { precision: 'date-only', time: undefined, name: undefined }
  }
}

/**
 * The inverse of {@link etaPatchFromTimeChoice}: derives the combined
 * picker's value from the current field so the control reflects stored
 * state. An `exact-time` whose `time` won't normalize round-trips back to
 * `invalid` (kept text + destructive ring).
 */
export function timeChoiceFromEtaField(
  field: EtaFieldValue
): EstimatedTimeChoice {
  switch (field.precision) {
    case 'date-only':
      return undefined
    case 'named-time':
      return field.name ? { kind: 'named', name: field.name } : undefined
    case 'exact-time': {
      if (!field.time) return undefined
      return normalizeTimeString(field.time)
        ? { kind: 'exact', time: field.time }
        : { kind: 'invalid', text: field.time }
    }
  }
}

/** The resolved API pair for one ETA field. */
export interface ResolvedEta {
  /** UTC instant for `etaAtPort` / `etaAtPilotStation`. */
  value: ISODateTimeString
  /** Value for the paired `*Precision` field. */
  precision: EtaPrecision
}

/**
 * Resolves an {@link EtaFieldValue} into the `{ instant, precision }`
 * pair the create-portcall payload expects, converting the local
 * wall-clock time into a UTC instant in the port's timezone.
 *
 * Returns `undefined` when the field is incomplete for its precision.
 *
 * @param field — the field state.
 * @param opts.timeZone — the port's IANA zone; nullish falls back to the
 *   current zone (per `combineLocalDateTimeToInstant`).
 * @param opts.referenceTime — the `localReferenceTime` resolved from the
 *   selected name; required for `named-time`.
 */
export function resolveEtaField(
  field: EtaFieldValue | undefined,
  opts: { timeZone?: string | null; referenceTime?: string }
): ResolvedEta | undefined {
  if (!field?.date) return undefined

  const combine = (time?: string): ISODateTimeString =>
    combineLocalDateTimeToInstant({
      date: field.date as ISODateTimeString,
      time,
      timeZone: opts.timeZone
    })

  switch (field.precision) {
    case 'date-only':
      return { value: combine('00:00'), precision: 'date-only' }
    case 'exact-time': {
      const time = normalizeTimeString(field.time ?? '')
      if (!time) return undefined
      return { value: combine(time), precision: 'exact-time' }
    }
    case 'named-time':
      if (!opts.referenceTime) return undefined
      return { value: combine(opts.referenceTime), precision: 'named-time' }
  }
}

/**
 * Why an ETA slot is invalid:
 * - `required` — the (single) ETA must be provided but is incomplete.
 * - `incomplete` — the slot was started but not finished for its precision.
 * - `at-least-one` — neither this slot nor its sibling is complete, and one
 *   of them must be (the pilot-station case).
 */
export type EtaValidationCode = 'required' | 'incomplete' | 'at-least-one'

/**
 * Validates one ETA slot against the wiki's precision rules. When the
 * port does not capture a pilot-station ETA the slot is simply required;
 * when it does, the slot may be empty as long as its sibling is complete,
 * but a partially-filled slot is always rejected. Returns `undefined`
 * when the slot is acceptable.
 */
export function validateEtaSlot(params: {
  value?: EtaFieldValue
  sibling?: EtaFieldValue
  pilotStationActive: boolean
}): EtaValidationCode | undefined {
  const { value, sibling, pilotStationActive } = params
  if (pilotStationActive) {
    if (!isEtaFieldEmpty(value) && !isEtaFieldComplete(value))
      return 'incomplete'
    if (!isEtaFieldComplete(value) && !isEtaFieldComplete(sibling))
      return 'at-least-one'
    return undefined
  }
  return isEtaFieldComplete(value) ? undefined : 'required'
}
